/**
 * fab-size-picker-collapsed.js
 *
 * Collapsed + tabbed size selector experiment. Works alongside fab-size-picker.js
 * which handles variant contract, room mock, and pill metadata.
 */
(function () {
  'use strict';

  function capture(eventName, props) {
    try {
      if (window.__fabTrackingHelpers && typeof window.__fabTrackingHelpers.capture === 'function') {
        window.__fabTrackingHelpers.capture(eventName, props || {});
      }
    } catch (e) { /* noop */ }
  }

  function parseFabBoolean(value, defaultValue) {
    if (typeof value === 'boolean') return value;
    if (value == null || value === '') return defaultValue;
    var s = String(value).trim().toLowerCase();
    if (s === 'false' || s === '0' || s === 'no' || s === 'off') return false;
    if (s === 'true' || s === '1' || s === 'yes' || s === 'on') return true;
    return defaultValue;
  }

  function sizeKeyForCell(cell) {
    if (!cell) return '';
    if (cell.dataset.fabSizeKey) return cell.dataset.fabSizeKey;
    if (window.FabSizeKey && cell.dataset.fabSizeValue) {
      return window.FabSizeKey.normalizePopularKey(cell.dataset.fabSizeValue);
    }
    return '';
  }

  class FabSizePickerCollapsed {
    /**
     * @param {HTMLElement} root
     * @param {import('./fab-size-picker.js')} picker — FabSizePicker instance
     */
    constructor(root, picker) {
      this.root = root;
      this.picker = picker;
      this.trigger = root.querySelector('[data-fab-collapsed-trigger]');
      this.panel = root.querySelector('[data-fab-collapsed-panel]');
      this.summaryEl = root.querySelector('[data-fab-collapsed-summary]');
      this.tabs = Array.from(root.querySelectorAll('[data-fab-size-tab]'));
      this.tabPanels = Array.from(root.querySelectorAll('[data-fab-size-tabpanel]'));
      this.collapseOnSelect = parseFabBoolean(root.dataset.fabCollapseOnSelect, true);
      this.defaultTab = root.dataset.fabDefaultTab === 'all' ? 'all' : 'popular';
      this.expanded = false;
      this.activeTab = this.tabs.length ? this.defaultTab : 'all';
      this._onKeydown = this.onKeydown.bind(this);
      this._exposureSent = false;

      this.init();
    }

    init() {
      if (!this.trigger || !this.panel) return;

      this.trigger.addEventListener('click', () => this.togglePanel());

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.activateTab(tab.dataset.fabSizeTab, true));
        tab.addEventListener('keydown', (e) => this.onTabKeydown(e, tab));
      });

      this.root.querySelectorAll('[data-fab-size-cell]').forEach((cell) => {
        cell.addEventListener('click', () => this.onPillSelected(cell));
      });

      if (this.picker && this.picker.select) {
        this.picker.select.addEventListener('change', () => this.syncSummaryFromSelect());
      }

      if (this.picker && this.picker.variantSelectsEl) {
        this.picker.variantSelectsEl.addEventListener('variant:change', () => {
          window.setTimeout(() => this.syncSummaryFromSelect(), 60);
        });
      }

      this.sendExposure();
      this.syncSummaryFromSelect();
    }

    sendExposure() {
      if (this._exposureSent) return;
      this._exposureSent = true;
      capture('size_selector_experiment_exposure', {
        variant: 'collapsed-tabbed',
        ui_variant: 'collapsed-tabbed',
        product_id: this.root.dataset.fabProductId || null,
      });
    }

    togglePanel(forceOpen) {
      var open = typeof forceOpen === 'boolean' ? forceOpen : !this.expanded;
      this.expanded = open;
      this.trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      this.panel.hidden = !open;

      if (open) {
        capture('size_selector_expanded', {
          variant: 'collapsed-tabbed',
          product_id: this.root.dataset.fabProductId || null,
        });
        var activeTabEl = this.tabs.find(function (t) {
          return t.dataset.fabSizeTab === this.activeTab;
        }, this);
        (activeTabEl || this.tabs[0])?.focus();
        document.addEventListener('keydown', this._onKeydown);
      } else {
        document.removeEventListener('keydown', this._onKeydown);
      }
    }

    collapsePanel() {
      if (!this.expanded) return;
      this.togglePanel(false);
      this.trigger.focus();
    }

    onKeydown(e) {
      if (!this.expanded) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        this.togglePanel(false);
        this.trigger.focus();
      }
    }

    activateTab(tabName, fromUser) {
      if (!tabName || this.tabs.length === 0) return;
      if (fromUser && tabName !== this.activeTab) {
        capture('size_selector_tab_changed', {
          tab: tabName,
          variant: 'collapsed-tabbed',
          product_id: this.root.dataset.fabProductId || null,
        });
      }
      this.activeTab = tabName;

      this.tabs.forEach(function (tab) {
        var isActive = tab.dataset.fabSizeTab === tabName;
        tab.classList.toggle('fab-size__tab--active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.tabIndex = isActive ? 0 : -1;
      });

      this.tabPanels.forEach(function (panel) {
        var isActive = panel.dataset.fabSizeTabpanel === tabName;
        panel.classList.toggle('fab-size__tabpanel--active', isActive);
        panel.hidden = !isActive;
      });
    }

    onTabKeydown(e, tab) {
      if (this.tabs.length < 2) return;
      var idx = this.tabs.indexOf(tab);
      if (idx < 0) return;

      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.preventDefault();
        var next = e.key === 'ArrowRight'
          ? (idx + 1) % this.tabs.length
          : (idx - 1 + this.tabs.length) % this.tabs.length;
        this.activateTab(this.tabs[next].dataset.fabSizeTab, true);
        this.tabs[next].focus();
      } else if (e.key === 'Home') {
        e.preventDefault();
        this.activateTab(this.tabs[0].dataset.fabSizeTab, true);
        this.tabs[0].focus();
      } else if (e.key === 'End') {
        e.preventDefault();
        this.activateTab(this.tabs[this.tabs.length - 1].dataset.fabSizeTab, true);
        this.tabs[this.tabs.length - 1].focus();
      }
    }

    onPillSelected(cell) {
      var tab = this.findTabForCell(cell);
      capture('size_selector_size_selected', {
        sizeKey: sizeKeyForCell(cell),
        tab: tab,
        fromCollapsed: true,
        panelOpen: this.expanded,
        variant: 'collapsed-tabbed',
        product_id: this.root.dataset.fabProductId || null,
      });

      window.requestAnimationFrame(() => {
        this.syncSummaryFromCell(cell);
        if (this.collapseOnSelect && this.expanded) {
          this.collapsePanel();
        }
      });
    }

    findTabForCell(cell) {
      var panel = cell.closest('[data-fab-size-tabpanel]');
      return panel ? panel.dataset.fabSizeTabpanel || 'all' : 'all';
    }

    syncSummaryFromSelect() {
      if (!this.picker || !this.picker.select || !this.summaryEl) return;
      var value = this.picker.select.value;
      var cells = this.root.querySelectorAll('[data-fab-size-cell]');
      var cell = null;
      for (var i = 0; i < cells.length; i++) {
        if (cells[i].dataset.fabSizeValue === value) {
          cell = cells[i];
          break;
        }
      }
      if (cell) {
        this.syncSummaryFromCell(cell);
      }
    }

    syncSummaryFromCell(cell) {
      if (!this.summaryEl || !cell) return;
      var dim = cell.dataset.fabDisplayDim || cell.querySelector('.fab-size__dim, .fab-size__asize-name')?.textContent?.trim() || '';
      var price = cell.querySelector('.fab-size__price, .fab-size__asize-price')?.textContent?.trim() || '';
      this.summaryEl.textContent = dim && price ? dim + ' · ' + price : dim || price;
    }
  }

  function initCollapsed(scope) {
    var roots = (scope || document).querySelectorAll(
      '[data-fab-size-picker][data-fab-size-mode="collapsed-tabbed"]'
    );
    roots.forEach(function (root) {
      if (root.__fabSizePickerCollapsed) return;
      var picker = root.__fabSizePicker;
      if (!picker) return;
      root.__fabSizePickerCollapsed = new FabSizePickerCollapsed(root, picker);
    });
  }

  window.__fabInitCollapsedSizePickers = initCollapsed;

  function boot() {
    initCollapsed(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    initCollapsed(e.target);
  });
  document.addEventListener('product-info:loaded', boot);
})();
